import { useState, useEffect, useRef, useCallback } from 'react';
import { getJob } from '../api/jobs.js';

// Stop polling after 35 minutes (slightly longer than max Cloud Run timeout)
const MAX_POLL_DURATION_MS = 35 * 60 * 1000;

export function useJobPolling(jobId, enabled = true) {
  const [job, setJob] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);
  const jobRef = useRef(null);
  const timeoutRef = useRef(null);
  const pollStartRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  useEffect(() => {
    if (!jobId || !enabled) {
      stopPolling();
      return;
    }

    let cancelled = false;

    async function poll() {
      // Stop polling if we've exceeded the max duration
      if (pollStartRef.current && Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        stopPolling();
        setJob((prev) => prev ? { ...prev, status: 'failed', error_message: 'Render timed out. Please try again.' } : prev);
        return;
      }

      try {
        const data = await getJob(jobId);
        if (cancelled) return;
        jobRef.current = data;
        setJob(data);
        setError(null);

        if (data.status === 'completed' || data.status === 'failed') {
          stopPolling();
          return;
        }

        // Schedule next poll with dynamic interval
        const interval = data.status === 'processing' ? 3000 : 10000;
        timeoutRef.current = setTimeout(poll, interval);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404) {
          stopPolling();
          setError('Job not found');
        } else {
          setError(err.message);
          // Retry on transient errors
          timeoutRef.current = setTimeout(poll, 10000);
        }
      }
    }

    setIsPolling(true);
    pollStartRef.current = Date.now();
    poll();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [jobId, enabled, stopPolling]);

  return { job, isPolling, error };
}
